// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';

/**
 * Fetches one cost currency's waterfall for a rollforward report. Each report
 * covers a single currency; the picker's options come from the org-wide
 * asset-currency list, so this only refetches when the active currency or the
 * date range changes.
 */
export function useRollforwardCurrencyQuery<T>(
  queryKeyBase: string,
  startDate: DateTime | null,
  endDate: DateTime | null,
  enabled: boolean,
  activeCurrencyCode: IsoCurrencyCode,
  fetchFn: (args: {
    startDate: DateTime;
    endDate: DateTime;
    currency: IsoCurrencyCode;
  }) => Promise<T>
): UseQueryResult<T> {
  return useQuery({
    queryKey: [
      queryKeyBase,
      startDate?.toISODate(),
      endDate?.toISODate(),
      activeCurrencyCode,
    ],
    queryFn: () => {
      if (!startDate || !endDate) {
        throw new Error('Start and end dates are required');
      }
      return fetchFn({ startDate, endDate, currency: activeCurrencyCode });
    },
    enabled: Boolean(startDate && endDate) && enabled,
  });
}
